import { useCallback, useState } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'

import { useAreaSelection } from '~/hooks/useAreaSelection'
import { useDayNavigation } from '~/hooks/useDayNavigation'
import type {
  SeriesKey,
  SeriesVisibility,
} from '~/pages/BalanceDemand/constants'
import { DEFAULT_SERIES_VISIBILITY } from '~/pages/BalanceDemand/constants'

type FilterState = {
  displayDate: Date
  seriesVisibility: SeriesVisibility
  visibleAreaCodes: Set<AreaCode>
  /**
   * エリアセレクターの選択肢 (= 対象エリア ∩ 業務エリア設定、issue #7535 /
   * conventions.md「業務エリアフィルタの原則」)。呼び出し元 `BalanceDemandPage`
   * が個人設定 (`useGetPersonalSettingsQuery`) から計算して渡す。取得中・エラー時の
   * フォールバックも呼び出し元の責務 (このフックは受け取った値をそのまま使う)。
   */
  selectableAreaCodes: readonly AreaCode[]
  isVoltageEnabled: boolean
  isAnalysisEnabled: boolean
  isDetailEnabled: boolean
}

type FilterActions = {
  setDisplayDate: (date: Date) => void
  goToToday: () => void
  toggleSeries: (key: SeriesKey) => void
  toggleVoltage: () => void
  toggleAnalysis: () => void
  toggleDetail: () => void
  setVisibleAreaCodes: (codes: Set<AreaCode>) => void
  /**
   * フィルタ群の指定だけを初期値へ戻す (「クリア」ボタン)。
   *
   * **表示日は戻さない。** 日を選び直すのは「絞り込みをやめる」ことではなく
   * 「別の日を見る」ことなので、絞り込みのクリアで巻き戻すと、日を送った後に
   * 系列を整理しただけで今日へ飛ばされる。design の相対取引一覧・アップロード
   * 履歴の「クリア」も対象日は据え置いている。
   *
   * **グラフ配置も戻さない。** そちらはカード外の「レイアウトリセット」が持つ
   * (8080 は 1 つのボタンで両方戻していたが、2026-09-07 のデザイナー判断で
   * 2 つに分けた)。
   */
  clearFilters: () => void
}

export type UseFilterStateReturn = FilterState & FilterActions

/**
 * 同時同量 - 需要のエリア選択の保存キー (AC-BDB-011 ※、issue #7606)。 レイアウトの保存値
 * (`clearGridCardLayouts`) と違ってログアウトで消さないのは、 中身が組織に依らない
 * エリアコードだけで、 次のログインユーザーに見えても困る情報を含まないため。
 */
export const AREA_SELECTION_STORAGE_KEY = 'ebalance-doji-bg-area-selection-v1'

export function useFilterState(
  selectableAreaCodes: readonly AreaCode[],
): UseFilterStateReturn {
  const { displayDate, setDisplayDate, goToToday } = useDayNavigation()
  const [seriesVisibility, setSeriesVisibility] = useState<SeriesVisibility>(
    DEFAULT_SERIES_VISIBILITY,
  )

  // AC-BDB-011 ※ の選択状態遷移 (初期値 = 選択肢全件 / 選択肢外を選択から
  // 落とす / クリアで未操作状態へ戻す) は需要・発電で共有の
  // `useAreaSelection` に統合済み (review 指摘 A-1/A-2)。
  const {
    selectableAreaCodes: stableSelectableAreaCodes,
    visibleAreaCodes,
    setVisibleAreaCodes,
    resetAreaSelection,
  } = useAreaSelection(selectableAreaCodes, AREA_SELECTION_STORAGE_KEY)

  // 電圧階級別は AC-BDB-003 / AC-BDB-016 で既定 ON (issue #4734)。
  const [isVoltageEnabled, setIsVoltageEnabled] = useState(true)
  // 分析補助は AC-BDB-003 / design bg.html のどちらも初期 ON (配下の気温が既定 ON
  // なので、親を OFF にすると ON の系列が操作不能になる)。
  const [isAnalysisEnabled, setIsAnalysisEnabled] = useState(true)
  // 詳細も AC-BDB-003 / design bg.html (`chk-detail` checked) のどちらも初期 ON。
  // 2026-08-12 の PdM 判断「ダッシュボードも詳細 ON」で決着した (issue #5088)。
  // 配下の実績値(補完)・露点が既定 ON なので、 親を OFF にすると ON の系列が
  // 操作不能になる (分析補助と同じ理由)。
  const [isDetailEnabled, setIsDetailEnabled] = useState(true)

  const toggleSeries = useCallback((key: SeriesKey): void => {
    setSeriesVisibility((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  // 3 つの親トグルはどれも子の値そのものを書き換えない。 書き換えると、 個別に
  // 消していた子が親を OFF→ON しただけで復活し、 AC-BDB-031 の「親 ON の時は
  // 各子トグルの ON/OFF に従う」 が壊れる。 チャートへ渡す時に親でゲートする
  // (詳細画面と同じ形)。
  const toggleVoltage = useCallback((): void => {
    setIsVoltageEnabled((prev) => !prev)
  }, [])

  const toggleAnalysis = useCallback((): void => {
    setIsAnalysisEnabled((prev) => !prev)
  }, [])

  const toggleDetail = useCallback((): void => {
    setIsDetailEnabled((prev) => !prev)
  }, [])

  // 初期値は 1 箇所に書いてあるものをそのまま使い直す (ここで別の既定を書くと、
  // 「クリアした状態」と「開いた直後の状態」が静かにズレる)。エリアは
  // `resetAreaSelection` (= 明示選択を未操作状態へ戻す) に委譲する —
  // `visibleAreaCodes` の導出が現在の `selectableAreaCodes` (AC-BDB-011 の
  // 初期値) を自動で使う。
  const clearFilters = useCallback((): void => {
    setSeriesVisibility(DEFAULT_SERIES_VISIBILITY)
    resetAreaSelection()
    setIsVoltageEnabled(true)
    setIsAnalysisEnabled(true)
    setIsDetailEnabled(true)
  }, [resetAreaSelection])

  return {
    displayDate,
    seriesVisibility,
    visibleAreaCodes,
    selectableAreaCodes: stableSelectableAreaCodes,
    isVoltageEnabled,
    isAnalysisEnabled,
    isDetailEnabled,
    setDisplayDate,
    goToToday,
    toggleSeries,
    toggleVoltage,
    toggleAnalysis,
    toggleDetail,
    setVisibleAreaCodes,
    clearFilters,
  }
}
